import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMyOrderDetail } from "../../../api/order";
import WithAuth from "../../../hoc/WithAuth";
import { Spin } from "antd";
import { InfoCard } from "../../../components/cards/info-card";
import {
  formatDateToEuropean,
  msToDaysHours,
} from "../../../util/date-formatter";
import { StatusBadge } from "../../../components/shared/status-badge";
import { FilterTabs } from "../../../components/ui/FilterTabs";
import FarmingStatus from "./FarmingStage";
import ContractList from "../../contract/ContractList";
import ValueChainExecuters from "./ValueChainExecuters";

const OrderDetailPage: React.FC = () => {
  const { id } = useParams();

  const [orderData, setOrderData] = useState<any>(null);
  const [activeFilter, setActiveFilter] = useState<string>("farmingStatus");

  /** API hooks */
  const { data: order, isLoading } = useMyOrderDetail(id || "");

  useEffect(() => {
    if (order?.data?.data) {
      setOrderData(order.data.data);
    }
  }, [order]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <h2 className="text-xl font-semibold text-gray-600">Order not found</h2>
      </div>
    );
  }

  const deliveryDays = msToDaysHours(orderData.deliveryDays);

  const statData = [
    {
      title: "Off Taker",
      value: orderData.orgName,
    },
    {
      title: "Product",
      value: orderData.product,
    },
    {
      title: "Quantity",
      value: orderData.quantity && `${orderData.quantity.toLocaleString()} kg`,
    },
    {
      title: "Status",
      value: (
        <StatusBadge
          value={
            orderData.status?.charAt(0).toUpperCase() +
            orderData.status?.slice(1).toLowerCase()
          }
        />
      ),
    },
    {
      title: "Start Date",
      value: formatDateToEuropean(orderData.startingDate),
    },
    {
      title: "Due Date",
      value: formatDateToEuropean(orderData.dueDate),
    },
  ];

  const filterTabs = [
    { value: "farmingStatus", label: "Farming Status" },
    { value: "contracts", label: "Contracts" },
    { value: "valueChainExecuters", label: "Value Chain Executers" },
  ];

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
  };

  return (
    <div
      className="bg-white px-4 md:px-8 py-8"
      style={{ fontFamily: "Poppins" }}
    >
      {/** Combined Information Section */}
      <div className="bg-white border border-[#E6EEF6] rounded-2xl p-6 w-full">
        {/** Basic Information */}
        <h2 className="text-base font-medium text-black mb-6">
          Basic Information
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-4 gap-x-6 mb-4">
          {statData.map((stat) => (
            <InfoCard title={stat.title} value={stat.value} />
          ))}
        </div>
      </div>

      {/** Delivery Days / Farmers / Contracts */}
      <div className="grid grid-cols-3 gap-[14px] mt-3">
        {/** Delivery Days */}
        <div className="flex flex-col md:flex-row bg-[#08453E] text-white rounded-2xl overflow-hidden">
          <div className="flex flex-1 justify-start items-center pl-6 py-4">
            <p className="text-base font-medium">Delivery Days</p>
          </div>
          <div className="flex items-center gap-1 pr-6 h-24">
            <TimeElapsed value={deliveryDays.days} unit="Days" />
            <TimeElapsed value={deliveryDays.hours} unit="Hours" />
          </div>
        </div>

        {/** Farmers */}
        <StatSummary label="Farmers" value={orderData.numberOfFarmers} />
        {/** Contracts */}
        <StatSummary label="Contracts" value={orderData.numberOfContracts} />
      </div>

      {/** Farming Section */}
      <div
        className="bg-transparent lg:bg-white mt-3"
        style={{
          borderRadius: 8.5,
          marginTop: 25,
        }}
      >
        {/** Header with Filters */}
        <div className="space-y-4 mb-6">
          {/** Filter Tabs - Desktop */}
          <FilterTabs
            tabs={filterTabs}
            activeTab={activeFilter}
            onTabChange={handleFilterChange}
            className="w-full lg:w-auto"
          />
        </div>

        {activeFilter === "farmingStatus" && <FarmingStatus />}
        {activeFilter === "contracts" && <ContractList />}
        {activeFilter === "valueChainExecuters" && <ValueChainExecuters />}
      </div>
    </div>
  );
};

export default WithAuth(OrderDetailPage);

type TimeElapsedProps = {
  value: number;
  unit: string;
};

export const TimeElapsed = ({ value, unit }: TimeElapsedProps) => {
  return (
    <div
      className={`flex flex-1 flex-col text-center rounded-lg bg-[#295C56] w-[70px] h-[70px] text-[0px] ${
        value >= 100 ? "min-w-max px-3" : ""
      }`}
    >
      <span className="text-[40px] font-medium">{value}</span>
      <span className="text-xs -mt-3">{unit}</span>
    </div>
  );
};

type StatSummaryProps = {
  label: string;
  value: number;
};

export const StatSummary = ({ label, value }: StatSummaryProps) => {
  return (
    <div className="bg-gray-50 border border-[#E6EEF6] rounded-2xl text-center py-4 flex justify-center items-center">
      <p className="text-base font-medium text-black mb-1">{label}</p>
      <p className="text-[40px] font-medium text-black w-[70px] h-[70px]">
        {value || 0}
      </p>
    </div>
  );
};
